import React, { useCallback, useEffect, useRef, useState } from "react";
import { Configure, InstantSearch, Hits, RefinementList, useInstantSearch, useSearchBox } from "react-instantsearch";
import AlgoliaPagination from "./AlgoliaPagination";
import {
  createQueryPageRouting,
  decodeEntities,
  trimExcerpt,
  useAlgoliaClient,
  useSearchInput,
} from "./utils";

const CustomSearchBox = ({ onScroll }) => {
  const { value, onChange, onReset, onSubmit } = useSearchInput({
    onChange: () => onScroll?.(),
    onSubmit: () => onScroll?.(),
    onReset: () => onScroll?.(),
  });

  return (
    <div className="ais-SearchBox">
      <form
        className="ais-SearchBox-form"
        role="search"
        noValidate
        onSubmit={onSubmit}
        onReset={onReset}
      >
        <input
          className="ais-SearchBox-input"
          type="search"
          value={value}
          onChange={onChange}
          placeholder="Ex. waterproofing, basement, etc…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
        {value ? (
          <button
            type="reset"
            className="ais-SearchBox-reset"
            aria-label="Clear search"
          >
            ✕
          </button>
        ) : null}
        <button
          className="ais-SearchBox-submit"
          type="submit"
          aria-label="Submit search"
        >
          Search
        </button>
      </form>
    </div>
  );
};

const blogPaginationHideMobile = (page, current, total) => {
  if (total <= 3) return false;
  if (current === 1 || current === 2) {
    return page > 3;
  }
  if (current === total || current === total - 1) {
    return page < total - 2;
  }
  return page < current - 1 || page > current + 1;
};

const HitCard = ({ hit }) => {
  const title = hit?.headline || hit?.title || hit?.pageHeadline || "Untitled";
  const url = hit?.url || "#";
  const excerpt =
    hit?.excerpt || hit?.pageIntroText || hit?.introText || hit?.contentBuilderContent || "";
  const imageUrl = hit?.imageUrl;
  const imageAlt = hit?.imageAltText || title;
  const servicesTitles = hit?.servicesTitles || [];
  const servicesSlugs = hit?.servicesSlugs || [];
  const servicesUrls = hit?.servicesUrls || [];
  const services = (servicesTitles.length ? servicesTitles : servicesSlugs).map(
    (label, idx) => ({
      label,
      url:
        servicesUrls[idx] ||
        (servicesSlugs[idx] ? `/services/${servicesSlugs[idx]}` : "#"),
    })
  ).slice(0, 1);
  const displayTitle = decodeEntities(title);

  return (
    <article className="b-searchable-blog-grid__hit">
      <a
        href={url}
        title={displayTitle}
      >
        {displayTitle}
      </a>
      {imageUrl ? (
        <div
          className="b-searchable-blog-grid__hit-image"
        >
          <img src={imageUrl} alt={imageAlt} loading="lazy" />
        </div>
      ) : null}
      <div className="b-searchable-blog-grid__hit-body">
        {services.length ? (
          <div className="c-services-tags">
            {services.map((service) => (
              <a
                className="c-services-tags__tag"
                key={service.label}
                href={service.url}
                title={service.label}
              >
                {service.label}
              </a>
            ))}
          </div>
        ) : null}
        <h4>{displayTitle}</h4>
        {excerpt ? <p>{trimExcerpt(excerpt, 160)}</p> : null}
      </div>
    </article>
  );
};

const SingleSelectFacets = ({
  attribute,
  label = "All Services",
  searchClient,
  indexName,
  onScroll,
}) => {
  const { indexUiState, setIndexUiState, results } = useInstantSearch();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [titlesReady, setTitlesReady] = useState(false);
  const titleCacheRef = useRef(new Map());
  useEffect(() => {
    const hits = results?.hits || [];
    hits.forEach((hit) => {
      const slugs = hit.servicesSlugs || [];
      const titles = hit.servicesTitles || [];
      slugs.forEach((slug, idx) => {
        const title = titles[idx];
        if (slug && title && !titleCacheRef.current.has(slug)) {
          titleCacheRef.current.set(slug, title);
        }
      });
    });
  }, [results]);

  // Prefetch titles map once from index to avoid slug flash
  useEffect(() => {
    if (!searchClient || !indexName) return;
    if (titleCacheRef.current.size > 0) {
      setTitlesReady(true);
      return;
    }
    let cancelled = false;
    searchClient
      .search([
        {
          indexName,
          params: {
            hitsPerPage: 100,
            attributesToRetrieve: ["servicesSlugs", "servicesTitles"],
            attributesToHighlight: [],
            attributesToSnippet: [],
          },
        },
      ])
      .then((res) => {
        if (cancelled) return;
        const hits = res?.results?.[0]?.hits || [];
        hits.forEach((hit) => {
          const slugs = hit.servicesSlugs || [];
          const titles = hit.servicesTitles || [];
          slugs.forEach((slug, idx) => {
            const title = titles[idx];
            if (slug && title && !titleCacheRef.current.has(slug)) {
              titleCacheRef.current.set(slug, title);
            }
          });
        });
        setTitlesReady(true);
      })
      .catch((error) => {
        console.warn("[SearchableBlogGrid] title prefetch failed", error);
        setTitlesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [indexName, searchClient]);

  const selected =
    (indexUiState?.refinementList?.[attribute] || [])[0] ?? null;

  useEffect(() => {
    if (!searchClient || !indexName) return;
    let cancelled = false;
    setLoading(true);
    searchClient
      .search([
        {
          indexName,
          params: {
            facets: [attribute],
            hitsPerPage: 0,
            maxValuesPerFacet: 50,
          },
        },
      ])
      .then((res) => {
        if (cancelled) return;
        const facetData = res?.results?.[0]?.facets?.[attribute] || {};
        const parsed = Object.entries(facetData).map(([value, count]) => ({
          value,
          label: value,
          count,
        }));
        parsed.sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );
        setOptions(parsed);
      })
      .catch((error) => {
        console.warn("[SearchableBlogGrid] facet fetch failed", error);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [attribute, indexName, searchClient]);

  useEffect(() => {
    const counts = results?.facets?.[attribute];
    if (!counts) return;
    setOptions((prev) =>
      prev.map((opt) => ({
        ...opt,
        count: counts[opt.value] ?? opt.count ?? 0,
      }))
    );
  }, [attribute, results]);

  const handleSelect = (value) => {
    setIndexUiState((ui) => ({
      ...ui,
      refinementList: {
        ...(ui?.refinementList || {}),
        [attribute]: value ? [value] : [],
      },
      page: 1,
    }));
    onScroll?.();
  };

  const orderedItems = options;
  const isAllSelected = !selected;
  const labelsReady =
    titlesReady ||
    (orderedItems.length > 0 &&
      orderedItems.every(
        (item) =>
          titleCacheRef.current.has(item.value) || typeof item.label === "string"
      ));

  return (
    <>
      <div className="b-searchable-blog-grid__facets">
        {loading || !labelsReady ? (
          <span className="ais-RefinementList-item ais-RefinementList-item--empty">
            Loading filters…
          </span>
        ) : (
          <>
            <button
              type="button"
              className={`ais-RefinementList-item ais-RefinementList-item--all${isAllSelected ? " ais-RefinementList-item--selected" : ""
                }`}
              onClick={() => handleSelect(null)}
            >
              {label}
            </button>
            {orderedItems.map((itm) => (
              <button
                key={itm.value}
                type="button"
                className={`ais-RefinementList-item${selected === itm.value
                  ? " ais-RefinementList-item--selected"
                  : ""
                  }`}
                onClick={() => handleSelect(itm.value)}
              >
                {titleCacheRef.current.get(itm.value) || itm.label}
              </button>
            ))}
          </>
        )}
      </div>
      {/* Hidden refinement list to register UI state for routing */}
      <div style={{ display: "none" }} aria-hidden="true">
        <RefinementList attribute={attribute} operator="or" />
      </div>
    </>
  );
};

const NoResults = ({ attribute, onScroll }) => {
  const { results, setIndexUiState } = useInstantSearch();
  const { refine: setQuery } = useSearchBox();

  const hasResults = results && results.nbHits > 0;

  const clearAll = () => {
    setQuery("");
    setIndexUiState((ui) => ({
      ...ui,
      query: "",
      refinementList: {
        ...(ui?.refinementList || {}),
        [attribute]: [],
      },
      page: 1,
    }));
    onScroll?.();
  };

  if (hasResults || !results) {
    return null;
  }

  return (
    <div className="b-searchable-blog-grid__no-results">
      <p>
        No articles match your filters.{" "}
        <button
          type="button"
          className="b-searchable-blog-grid__clear-link"
          onClick={clearAll}
        >
          Clear filters
        </button>
      </p>
    </div>
  );
};


export default function SearchableBlogGrid({
  appId: propAppId,
  searchKey: propSearchKey,
  indexName: propIndexName,
  hitsPerPage = 18,
  facets: facetsProp,
} = {}) {
  const { client: searchClient, appId, searchKey } = useAlgoliaClient(
    propAppId,
    propSearchKey
  );
  const indexName = propIndexName ?? "blog";
  const facets = facetsProp ?? ["servicesSlugs"];

  if (!appId || !searchKey || !indexName || !searchClient) {
    return null;
  }

  const containerRef = useRef(null);
  const anchorRef = useRef(null);

  const scrollToGrid = useCallback(() => {
    if (typeof window === "undefined") return;
    const el = anchorRef.current || containerRef.current;
    if (!el) return;
    window.requestAnimationFrame(() => {
      const top = el.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top,
        behavior: "smooth",
      });
    });
  }, []);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      routing={createQueryPageRouting({
        indexName,
        mapStateToRoute(state, base) {
          const service =
            state.refinementList?.[facets[0]] &&
              state.refinementList[facets[0]][0]
              ? state.refinementList[facets[0]][0]
              : undefined;
          return {
            ...base,
            service,
          };
        },
        mapRouteToState(routeState) {
          return {
            refinementList: {
              [facets[0]]: routeState.service ? [routeState.service] : [],
            },
          };
        },
      })}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <Configure hitsPerPage={hitsPerPage} />
      <div className="b-searchable-blog-grid__inner" ref={containerRef}>
        <div className="b-searchable-blog-grid__anchor" ref={anchorRef} aria-hidden="true" />
        <div className="b-searchable-blog-grid__header">
          <h2>More Articles</h2>
          <SingleSelectFacets
            attribute={facets[0]}
            searchClient={searchClient}
            indexName={indexName}
            onScroll={scrollToGrid}
          />
          <div className="b-searchable-blog-grid__search">
            <span>Search all articles</span>
            <CustomSearchBox onScroll={scrollToGrid} />
          </div>
        </div>

        <NoResults attribute={facets[0]} onScroll={scrollToGrid} />

        <div className="b-searchable-blog-grid__results">

          <div className="b-searchable-blog-grid__hits">
            <Hits hitComponent={HitCard} />
          </div>

          <AlgoliaPagination
            onScroll={scrollToGrid}
            withEdgeClass
            hideMobilePredicate={blogPaginationHideMobile}
          />
        </div>
      </div>
    </InstantSearch>
  );
}
